'use client'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Tab,
  AlertTitle,
  Alert,
  Stack,
  Typography,
  Chip, TextField
} from '@mui/material'
import SnackBar from 'src/components/era-package/era/SnackBar'
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection'
import Button from 'src/components/era-package/forms/Button'
import Buttons from 'src/components/era-package/era/Buttons'
import { useUnsavedAlert } from 'src/utils/era-package/setupUnsavedAlert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faClose,
  faFilePen,
  faFloppyDisk as saveIcon,
  faList,
  faPlus,
  faSpinnerThird,
  faTrash,
  faEdit, faAngleRight as nextIcon
} from '@fortawesome/pro-regular-svg-icons'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import {
  addTabKeyToFields,
  addTabKeyToOptions
} from 'src/utils/era-package/tab-utils'
import {
  feeCodeOptionsMap,
  lineItemTariff,
  party,
  partyTypeOptionsMap,
  pga,
  pgaContainer,
  pgaLaceyActFields,
  pgaLaceyActHarvestFields,
  pgaLaceyActPartyContactFields,
  pgaLaceyActPartyFields,
  pgaOptionMap,
  pgaTscaOdsHeader,
  pgaTscaOdsIndividual,
  tariff,
  userFee,
  harvestCountryOptionMap
} from '../../../fields/entry-summary-line'
import DataTableSection from '../../../../../components/era-package/forms/DataTableSection'
import AbiEntrySummaryApiUrls from '../../../../../apiUrls/AbiEntrySummaryApiUrls'
import AbiSKUMgmtApiUrls from '../../../../../apiUrls/AbiSKUMgmtApiUrls';
import SKUMgmtLookupDialog from '../../../../../views/abi/component/SKUMgmtLookupDialog';
import { FileForUserGetPartyId } from '../../../../../common/FileForUserCommon';
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { createColumnHelper } from '@tanstack/react-table'
import Spinner from '../../../../../components/era-package/era/Spinner'
import {
  countryOriginOptionsMap,
  fdaAffirmationCompliance, fdaConstituent, fdaContainer,
  fdaCountryOrigin,
  fdaLACF, fdaLotInformation, fdaLotInformationDRU, fdaOptionsMap, fdaPackaging, fdaPartiesPointContact,
  fdaPrivateOwnedVehicleInformation,
  fdaRemark, lotInfoOptionsMap, partiesOptionsMap,
  pgaFdaHeader
} from "../../../fields/entry-summary-line-pga";

const EditESLineModal = ({
  title = 'Record',
  open,
  onClose,
  onSave,
  onDelete,
  handleRefreshData,
  fields,
  formFields,
  name,
  optionsMap,
  initialData,
  isEditing,
  index,
  initialSkuId,
  footerRender
}) => {
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [hasUnsaved, setHasUnsaved] = useState(false)
  const rhfArray = useFieldArray({ name }) || null
  const rhf = useFormContext() || null
  const [resetData, setResetData] = useState(null) // need to cache this data; formData & initialData will change
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [showFormErrorSnackbar, setShowFormErrorSnackbar] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const [value, setValue] = React.useState('basic');
  const [editDialogOpen, setEditDialogOpen] = React.useState(false)
  const [pgaField, setPgaField] = React.useState([])
  const [indexedPgaOptionMap, setIndexedPgaOptionMap] = React.useState([])
  const [formSectionPga, setFormSectionPga] = React.useState('')
  const [indexedPgaName, setIndexedPgaName] = React.useState('')
  const [SKULookupOptions, setSKULookupOptions] = useState([]);
  const [SKULookupLoaded, setSKULookupLoaded] = useState(false);
  const [SKUlookupDialogOpen, setSKULookupDialogOpen] = useState(false);
  const [display, setDisplay] = React.useState(false)
  const [lookupSearchText, setLookupSearchText] = useState('');
  const [openAddHtsDialog, setOpenAddHtsDialog] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)

  const fieldIndex = isEditing ? index : rhfArray?.fields?.length
  const indexedName = `${name}.${fieldIndex}`
  const isSKUModal = true

  
  const skuIdNormalized = useMemo(() => {
    if (initialSkuId === null || initialSkuId === undefined || initialSkuId === '') return null;
    const n = Number(initialSkuId);
    return Number.isFinite(n) ? n : null;
  }, [initialSkuId]);
  const isNewSku = skuIdNormalized === 0;                // "new" SKU template flow
  const isExistingSku = skuIdNormalized != null && skuIdNormalized > 0; // existing SKU


  // watch pgaList
  const pgaList = useWatch({
      control: rhf.control,
      name: `${indexedName}.pgaList`
    }) || []

  const pgaData = useMemo(() => pgaList.map((r) => ({ ...r })), [pgaList])

  // watch tariffList
  const tariffList = useWatch({
    control: rhf.control,
    name: `${indexedName}.tariffList`,
  });

  const skuLookupValue = useWatch({
    control: rhf?.control,
    name: `${indexedName}.skuLookup`
  })

  useEffect(() => {
    if (open && isSKUModal && !SKULookupLoaded) {
      const loadSKULookupOptions = async () => {
        try {
          // Fetch trade partners directly from API
          const response = await fetch(
            AbiSKUMgmtApiUrls.GET + `?partyId=${FileForUserGetPartyId()}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
              }
            }
          )
          
          if (response.ok) {
            const json = await response.json()
            const skus = json?.data || []
            // Map to format needed for autocomplete
            const options = skus.map(sku => ({
              value: sku.id?.toString() || sku.skuReference,
              skuReference: sku.skuReference,
              name: sku.skuDesc,
              description: sku.skuReference || sku.skuDesc,
              ...sku
            }))
            setSKULookupOptions(options)
            setSKULookupLoaded(true)
          } else {
            console.error('Error fetching skus')
            setSKULookupOptions([])
          }
        } catch (error) {
          console.error('Error loading sku lookup options:', error)
          setSKULookupOptions([])
        }
      }
      loadSKULookupOptions()
    }
  }, [open, isSKUModal, SKULookupLoaded])

  useEffect(() => {
    if (!open) {
      setSKULookupLoaded(false)
    }
  }, [open])

  
  useEffect(() => {
     if (!open || !initialSkuId) return;
    // Reuse existing handler; it fetches details via ?id=...
    handleSKUSelect({ id: initialSkuId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSkuId]);

  useEffect(() => {
    if (!skuLookupValue || !isSKUModal || !rhf || SKULookupOptions.length === 0) return

    // Find the selected party from options
    const selectedSKU = SKULookupOptions.find(opt => opt.value === skuLookupValue)
    
    if (selectedSKU?.data) {
      const data = selectedSKU.data
      
      setHasUnsaved(true)
    }
  }, [skuLookupValue])
  
  // watch dutyAmt INSIDE tariffList which htsNumber length (base) != 10
  const watchDutyAmt = useMemo(() => {
    if (!tariffList) return [];
    return tariffList.filter(item => item.htsNumber?.length !== 10).map(item => item.dutyAmt);
  }, [
    tariffList
      ?.map(item => {
        if (item.htsNumber?.length !== 10 && item.dutyAmt != null) {
          return item.dutyAmt;
        }
        return null;
      }).filter(Boolean).join("|")
  ]);

  // default esline number
  useEffect(() => {
    if (rhf.getValues(`${indexedName}.esLine`) == null && open) {
      rhf.setValue(`${indexedName}.esLine`, Number(fieldIndex) + 1)
    }

  }, [open]);

  // calculate dutyAmt base on goodsValue of base hts in tarifflist
  useEffect(() => {
    if(tariffList === undefined) return;

    if (initialData?.tariffList) {
      rhf.setValue(`${indexedName}.dutyAmtSum`, initialData.tariffList.reduce((sum, hts) => sum + Number(hts.dutyAmt), 0).toFixed(2))
    }

  }, [watchDutyAmt]);

  // calculate user fees base on mpf / hmf toggle switch
  const watchMPF = useWatch({ control: rhf.control, name: `${indexedName}.includeMPFFee` });
  const watchHMF = useWatch({ control: rhf.control, name: `${indexedName}.includeHMFFee` });

  useEffect(() => {
    let userFeeList = rhf.getValues(`${indexedName}.userFeeList`)
    const goodsValue = rhf.getValues(`${indexedName}.goodsValue`)

    if(userFeeList === undefined || goodsValue === undefined) return;

    if(rhf.getValues(`${indexedName}.includeMPFFee`) && (!userFeeList?.some(userFee => userFee.feeCode === '499'))) {
      userFeeList = [...userFeeList, { feeCode: '499', fee:(goodsValue*0.003464).toFixed(2)}]
    } else if(!rhf.getValues(`${indexedName}.includeMPFFee`) && rhf.getValues(`${indexedName}.includeMPFFee`) !== undefined) {
      userFeeList = userFeeList.filter(item => item.feeCode !== '499');
    }

    if(rhf.getValues(`${indexedName}.includeHMFFee`) && (!userFeeList?.some(userFee => userFee.feeCode === '501'))) {
      userFeeList = [...userFeeList, { feeCode: '501', fee:(goodsValue*0.00125).toFixed(2)}]
    } else if(!rhf.getValues(`${indexedName}.includeHMFFee`) && rhf.getValues(`${indexedName}.includeHMFFee`) !== undefined) {
      userFeeList = userFeeList.filter(item => item.feeCode !== '501');
    }

    rhf.setValue(`${indexedName}.userFeeList`, userFeeList)
  }, [watchMPF, watchHMF]);

  const fetchHtsCode = async (htsList) => {
    try {
      setLoading(true)
      const response = await fetch(AbiEntrySummaryApiUrls.GET_HTS_CODE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify(htsList)
      })

      if (!response.ok) {
        setLoading(false)
        console.error('Error fetching..')
      } else {
        const json = await response.json();

        const htsCode = json?.data?.find(item => item?.tariffNumber?.length === 10);

        if (htsCode) await fetchPgaFlags(htsCode?.pgaFlags)

        setDisplay(
          htsCode?.countervailingDutyFlag ||
            htsCode?.antidumpingDutyFlag ||
            htsCode?.quotaIndicator ||
            htsCode?.miscPermitLicenseIndicator
        )

        rhf.setValue(`${indexedName}.htsCode`, htsCode)

        const tariffList = rhf.getValues(`${indexedName}.tariffList`) || [];
        const goodsValue = rhf.getValues(`${indexedName}.goodsValue`) || 0;

        const updatedTariffList = tariffList.map(item => {
          const matchingTariff = Array.isArray(json?.data) ? json?.data?.find(h => h?.tariffNumber === item.htsNumber) : null;

          if (matchingTariff) {
            return {
              ...item,
              goodsValue: item?.htsNumber?.length === 10 ? goodsValue : item?.goodsValue,
              uom1: matchingTariff.unit1,
              uom2: matchingTariff.unit2,
              uom3: matchingTariff.unit3,
              tariffDescription: matchingTariff.description
                ?.replaceAll('&lt;', '<')
                .replaceAll('&gt;', '>')
                .replaceAll('&amp;', '&')
            };
            }

          return item;
        });

        rhf.setValue(`${indexedName}.tariffList`, updatedTariffList);
        setLoading(false)

        return json.data
      }
    } catch (error) {
      setLoading(false)
      console.error('Error fetching..')
    }
  };

  const fetchPgaFlags = async (pgaFlags) => {
    try {
      setLoading(true)
      const response = await fetch(
        `${AbiEntrySummaryApiUrls.GET_PGA_FLAG_CODE}?pgaFlags=${pgaFlags}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization:
              'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
          }
        }
      )

      if (!response.ok) {
        setLoading(false)
        console.error('Error fetching..')
      } else {
        setLoading(false)
        const json = await response.json()
        const pgaFlagRes = json?.data

        rhf.setValue(`${indexedName}.pgaFlag`, pgaFlagRes)

        const counts = pgaFlagRes.reduce((acc, item) => {
          acc[item.tariffFlagCode] = (acc[item.tariffFlagCode] || 0) + 1
          return acc
        }, {})

        const uniqueList = pgaFlagRes.filter(
          (item) => counts[item.tariffFlagCode] === 1
        )
        const duplicateList = Object.values(
          pgaFlagRes
            .filter((item) => counts[item.tariffFlagCode] > 1)
            .reduce((acc, item) => {
              acc[item.tariffFlagCode] = item // keep only one per code
              return acc
            }, {})
        )

        const pgaList = rhf.getValues(`${indexedName}.pgaList`) || []

        if (pgaList.length === 0) {
          const pgaUniqueList = uniqueList.map((item) => ({
            agencyCode: item.agencyCode,
            programCode: item.programCode
          }))

          const pgaDuplicateList = duplicateList.map((item) => ({
            agencyCode: item.agencyCode
          }))

          rhf.setValue(`${indexedName}.pgaList`, [
            ...pgaUniqueList,
            ...pgaDuplicateList
          ])
        }

        return json.data
      }
    } catch (error) {
      setLoading(false)
      console.error('Error fetching..')
    }
  }

  const handleChange = (event, newValue) => {
    const htsList = rhf
      .getValues(`${indexedName}.tariffList`)
      ?.map((item) => item?.htsNumber)

    fetchHtsCode(htsList)
    setValue(newValue)
  }

  // Refs for focus management
  const dialogRef = useRef(null)

  // Set up unsaved alert functionality
  useUnsavedAlert(hasUnsaved)

  const modalFields = useMemo(() => {
    return !!rhfArray && Array.isArray(formFields)
      ? formFields.map((field) => {
          let copy = { ...field }
          copy.name = `${indexedName}.${copy.name}`

          if (field.type === 'lookupButton' && isSKUModal) {
            copy.onLookupClick = () => {
              setSKULookupDialogOpen(true)
            }
            copy.onLookupSelect = (partner) => {
              handleSKUSelect(partner)
            }
        }

          if (field.name === 'saveSKUTemplate' && initialSkuId != null) {
            copy.disabled = true
          }

          if (field.name === 'skuReference' && initialSkuId != null) {
            copy.disabled = true;          
          }
          
          if (field.name === 'skuReference' && isNewSku) {
            copy.disabled = false;          // editable
            copy.required = true;           // show required asterisk
            copy.requiredCondition = undefined; // force required regardless of any original condition
          }

          return copy
        })
      : fields
  }, [formFields, indexedName, rhfArray, isSKUModal, initialSkuId, isNewSku]);

  const indexedOptions = useMemo(() => {
    const baseOptions = isSKUModal 
      ? { ...optionsMap, skuLookup: SKULookupOptions }
     : optionsMap
    return !!rhfArray ? addTabKeyToOptions(baseOptions, indexedName) : baseOptions
  }, [optionsMap, SKULookupOptions, indexedName, rhfArray, isSKUModal])

  // Comprehensive focus management to prevent aria-hidden conflicts
  useEffect(() => {
    if (open) {
      // Immediately blur all focused elements in main wrapper when modal opens
      const mainWrapper = document.querySelector('.mainwrapper')
      if (mainWrapper) {
        const allFocusedElements = mainWrapper.querySelectorAll(':focus')
        allFocusedElements.forEach((element) => {
          element.blur()
        })

        // Also blur any buttons that might retain focus
        const allButtons = mainWrapper.querySelectorAll(
          'button, [role="button"]'
        )
        allButtons.forEach((button) => {
          if (document.activeElement === button) {
            button.blur()
          }
        })
      }

      // Set up continuous monitoring to catch any focus that slips through
      const focusMonitor = setInterval(() => {
        const mainWrapper = document.querySelector('.mainwrapper')
        const focusedElement = document.activeElement

        if (
          mainWrapper &&
          mainWrapper.getAttribute('aria-hidden') === 'true' &&
          focusedElement &&
          mainWrapper.contains(focusedElement)
        ) {
          // Immediately blur the problematic element
          focusedElement.blur()

          // Force focus to modal
          const dialog = dialogRef.current
          if (dialog) {
            const firstFocusable = dialog.querySelector(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
            if (firstFocusable) {
              firstFocusable.focus()
            }
          }
        }
      }, 50) // More frequent monitoring

      return () => {
        clearInterval(focusMonitor)
      }
    }
  }, [open])

  useEffect(() => {
    if (initialData) {
      setFormData(initialData)
      setResetData({ ...initialData })
    } else {
      // Initialize with default values
      const defaults = {}
      fields.forEach((field) => {
        defaults[field.name] = field.defaultValue
      })
      setFormData(defaults)
    }
    // Reset unsaved state when modal opens/closes or initial data changes
    setHasUnsaved(false);
  }, [initialData, fields, open]);


  const [indexedPgaRowIndex, setIndexedPgaRowIndex] = useState(null);

  useEffect(() => {
    if (indexedPgaRowIndex == null) return

    const srcPath = `${indexedName}.pgaList.${indexedPgaRowIndex}.tscaOdsProgramCode`
    const dstPath = `${indexedName}.pgaList.${indexedPgaRowIndex}.programCode`

    const tscaCode = rhf.watch(srcPath)

    // Mirror to programCode only if changed
    const currentProgramCode = rhf.getValues(dstPath)

    if (tscaCode != null && currentProgramCode !== tscaCode) {
      rhf.setValue(dstPath, tscaCode, {
        shouldValidate: true,
        shouldDirty: true
      })
    }
  }, [
    indexedPgaRowIndex,
    indexedName,
    rhf.watch(`${indexedName}.pgaList.${indexedPgaRowIndex}.tscaOdsProgramCode`)
  ])

  const validateForm = async () => {
    const newErrors = {}

    if (!!rhf) {
      if (rhf.getValues(indexedName) !== undefined) {
        if (isSKUModal) {
          
        if (Number(initialSkuId) === 0) {
            const refValue = rhf.getValues(`${indexedName}.skuReference`);
            if (!refValue || !String(refValue).trim()) {
              rhf.setError(`${indexedName}.skuReference`, {
                type: 'required',
                message: 'SKU Reference is required',
              });
              return false;  // <-- block Add/Save
            }
        }

          const currentData = rhf.getValues(indexedName)
          if (!isNewSku && !isExistingSku && currentData?.saveSKUTemplate && !currentData?.skuReference?.trim()) {
            rhf.setError(`${indexedName}.skuReference`, {
              type: 'required',
              message: 'SKU Reference is required when saving to SKU'
            })
            return false
          }
        }
        const err = await rhf.trigger(indexedName)
        return err
      } else {
        // item is empty.
        setShowErrorSnackbar(true)
        return false
      }
    }

    // no RHF. Manual validation
    modalFields.forEach((field) => {
      if (field.required) {
        const value = formData[field.name]
        if (!value || String(value).trim() === '') {
          newErrors[field.name] = `${field.label} is required`
        }
      }
    })

    setErrors(newErrors)
    console.log('errors', newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    let isValid = await validateForm()
    if (isValid) {
      setLoading(true)
      setHasUnsaved(false) // Clear unsaved flag on successful save
      let toSend = !!rhf ? rhf.getValues(indexedName) : formData

      if (toSend.abiTransId === null || toSend.abiTransId === undefined)
        toSend = { ...toSend, abiTransId: rhf.getValues('header.abiTransId') }

      
      if (initialSkuId != null && initialSkuId !== '') {
        // pick the key your API expects:
        toSend = { ...toSend, skuId: initialSkuId };
        // or: toSend = { ...toSend, initialSkuId };
      }


      try {
        const response = await fetch(AbiEntrySummaryApiUrls.SAVE_LINE_ITEM, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization:
              'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
          },
          body: JSON.stringify(toSend)
        })

        if (!response.ok) {
          console.log('Error saving file')
          setShowFormErrorSnackbar(true)
          setLoading(false)
          return
        }

        const result = await response.json()
        console.log('File saved successfully:', result)
        onClose()
        setValue('basic')
        handleRefreshData()
        setShowSuccessSnackbar(true)
        setLoading(false)
      } catch (error) {
        console.log('Error saving file')
        setShowFormErrorSnackbar(true)
        setLoading(false)
      }
    }
  }

  const handleClose = () => {
    if (hasUnsaved) {
      const message =
        'You have unsaved changes. Are you sure you want to close without saving?'
      if (window.confirm(message)) {
        setHasUnsaved(false)
        if (!!rhfArray) {
          if (isEditing)
            // reset data for this index
            rhfArray.update(fieldIndex, resetData)
          else rhfArray.remove(fieldIndex)
        }
        // set tab back to basic
        setValue('basic')
        onClose()
      }
    } else {
      // set tab back to basic
      setValue('basic')
      onClose()
    }
  }

  const handleEditDialogClose = () => {
    setEditDialogOpen(false)
  }

  const handleDutyCalculation = async () => {
    setDataLoading(true);
    let toSend = !!rhf ? rhf.getValues(indexedName) : formData

    try {
      const response = await fetch(AbiEntrySummaryApiUrls.DUTY_CALCULATE_LINE_ITEMS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify(toSend)
      });

      if (!response.ok) {
        console.log('Error calculate duty');
        setDataLoading(false);
        return;
      }

      const result = await response.json();
      console.log('Duty calculated successfully:', result);
      rhf.setValue(`${indexedName}.tariffList`, result.data.tariffList);
      setDataLoading(false);
    } catch (error) {
      console.log('Error calculate duty');
      setDataLoading(false);
    }
  };

  // Handle keyboard navigation for accessibility
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose()
    } else if (event.key === 'Tab') {
      // Ensure focus stays within the modal
      const dialog = dialogRef.current
      if (dialog) {
        const focusableElements = dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault()
            lastElement.focus()
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault()
            firstElement.focus()
          }
        }
      }
    }
  }

  const handleSKUSelect = (data) => {
    if (!data || !rhf) return
    
    setLoading(true)
    fetch(AbiSKUMgmtApiUrls.GET + `?id=${data.id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization:
              'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
          }
        })
          .then((res) => res.json())
          .then((json) => {
            setLoading(false)

            const lineId = rhf.getValues(`${indexedName}.id`);
            const esLine = rhf.getValues(`${indexedName}.esLine`);
            const saveSKUTemplate = rhf.getValues(`${indexedName}.saveSKUTemplate`);
            const skuReference = rhf.getValues(`${indexedName}.skuReference`);
            const abitransId = rhf.getValues(`header.abiTransId`);
   
            rhf.setValue(indexedName, json.data.lineitemsDTO ?? {}, { shouldDirty: true })
            rhf.setValue(`${indexedName}.id`, lineId || null, { shouldDirty: true })
            rhf.setValue(`${indexedName}.esLine`, esLine || '', { shouldDirty: true })
            rhf.setValue(`${indexedName}.abiTransId`, abitransId || null, { shouldDirty: true })
            rhf.setValue(`${indexedName}.saveSKUTemplate`, saveSKUTemplate || null, { shouldDirty: true })
            rhf.setValue(`${indexedName}.skuReference`, skuReference || null, { shouldDirty: true })
            setHasUnsaved(true)
            
          })
          .catch((err) => {
            setLoading(false)
            console.error(err)
          })
  }

  const handleHtsAdd = (row) => {
    console.log(row)
    console.log(rhf?.getValues(indexedName)?.tariffList)
    // Get the current list
    let htsList = rhf?.getValues(`${indexedName}.tariffList`) || [];
    htsList = [...htsList, {htsNumber: row.tariffNumber}];
    rhf.setValue(`${indexedName}.tariffList`, htsList);
    setOpenAddHtsDialog(false)
  }

  const columnHelper = createColumnHelper()

  const operationColumn = [
    columnHelper.accessor(`disclaimer`, {
      header: 'Disclaimer',
      cell: (info) => {
        return (
          <Stack direction='row' spacing={2} alignItems='center'>
            <Typography variant='body2' noWrap>
              {info.getValue()}
            </Typography>
            <Chip
              label={
                rhf.getValues(`${indexedName}`)?.pgaFlag?.[0]?.requiredFlag ===
                'M'
                  ? 'Disclaim'
                  : 'Required'
              }
              size='small'
            />
          </Stack>
        )
      }
    }),
    {
      id: 'operationColumn',
      header: () => 'Operation',
      cell: ({ row }) => {
        let title = ''
        const flagCode =
          rhf
            .getValues(`${indexedName}`)
            ?.pgaFlag?.find(
              (flag) => flag.agencyCode === row.original?.agencyCode
            )?.tariffFlagCode ?? ''

                if (
          ['EP1', 'EP2', 'EP7', 'EP8'].some((code) => flagCode.includes(code))
        ) {
          title = 'Edit TSCA & ODS'
        } else if (['AL1', 'AL2'].some((code) => flagCode.includes(code))) {
          title = 'Edit Lacey Act'
        } else if (['FD1', 'FD2', 'FD3', 'FD4'].some((code) => flagCode.includes(code))) {
          title = 'Edit FDA'
        } else if (row.original?.agencyCode === 'FDA') {
          // Temporary bypass: show Edit FDA if agencyCode is FDA even without pgaFlag
          title = 'Edit FDA'
        } else if (['EP', 'EPA'].includes(row.original?.agencyCode)) {
          // Temporary bypass for TSCA & ODS
          title = 'Edit TSCA & ODS'
        } else if (row.original?.agencyCode === 'APH') {
          // Temporary bypass for Lacey Act
          title = 'Edit Lacey Act'
        } else {
          return ''
        }

        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'left',
              height: '100%'
            }}
            onClick={(e) => {
              e.stopPropagation() // Prevent row click
              const pgaName = `${indexedName}.pgaList.${row.index}`
              setIndexedPgaName(pgaName)
              setIndexedPgaRowIndex(row.index) // <-- capture the index

              const pgaFields = [
                ...pgaTscaOdsHeader,
                ...pgaTscaOdsIndividual,
                ...pgaLaceyActFields,
                ...pgaLaceyActPartyFields,
                ...pgaFdaHeader,
                ...fdaRemark,
                ...fdaPrivateOwnedVehicleInformation,
                ...fdaLACF
              ]
              setPgaField(addTabKeyToFields(pgaFields, pgaName))
              setIndexedPgaOptionMap(addTabKeyToOptions(pgaOptionMap, pgaName))

              const flags =
                rhf.getValues(`${indexedName}`)?.htsCode?.pgaFlags ?? []
                            if (
                ['EP1', 'EP2', 'EP7', 'EP8'].some((code) =>
                  flagCode.includes(code)
                )
              ) {
                setFormSectionPga('TSCA ODS')
              } else if (
                ['AL1', 'AL2'].some((code) => flagCode.includes(code))
              ) {
                const dstPath = `${pgaName}.programCode`
                const programCode = rhf.getValues(dstPath)
                if(programCode == '' || programCode == null) {
                  rhf.setValue(dstPath, 'APL', {
                    shouldValidate: true,
                    shouldDirty: true
                  })
                }
                setFormSectionPga('Lacey Act')
              } else if (
                  ['FD1', 'FD2', 'FD3', 'FD4'].some((code) => flagCode.includes(code))
                ) {
                setFormSectionPga('FDA')
              } else if (row.original?.agencyCode === 'FDA') {
                // Temporary bypass: open FDA dialog if agencyCode is FDA
                setFormSectionPga('FDA')
              } else if (['EP', 'EPA'].includes(row.original?.agencyCode)) {
                // Temporary bypass for TSCA & ODS
                setFormSectionPga('TSCA ODS')
              } else if (row.original?.agencyCode === 'APH') {
                // Temporary bypass for Lacey Act
                setFormSectionPga('Lacey Act')
              }

              setEditDialogOpen(true)
            }}
          >
            {' '}
            {title}
            <FontAwesomeIcon
              icon={faEdit}
              style={{ marginLeft: 8, cursor: 'pointer' }}
            />
          </Box>
        )
      },
      enableSorting: false,
      enableFiltering: false
    }
  ]

  const formSections = [
    {
      title: 'SKU Template',
      fields: modalFields.filter((field) => field.section === 'skutemplate')
    },
    {
      title: 'Basic',
      fields: modalFields.filter((field) => field.section === 'basic')
    },
    {
      title: 'HTS',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedName}.tariffList`,
      tableComponent: DataTableSection,
      tableFields: lineItemTariff,
      maxRows: 999,
      showHtsLookup: true,
      htsRowClick: (row) => {
        setSelectedRow(row)
        setOpenAddHtsDialog(true)
      }
    },
    {
      title: 'Fee',
      fields: modalFields.filter((field) => field.section === 'fee'),
      optionsMap: indexedOptions
    },
    {
      title: 'Fee List',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedName}.userFeeList`,
      tableComponent: DataTableSection,
      tableFields: userFee,
      maxRows: 999,
      optionsMap: feeCodeOptionsMap
    },
    {
      title: 'Parties',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedName}.cargoEntityList`,
      tableComponent: DataTableSection,
      tableFields: party,
      maxRows: 999,
      optionsMap: partyTypeOptionsMap
    },
    {
      title: 'Miscellaneous',
      fields: modalFields.filter((field) => field.section === 'misc'),
      optionsMap: indexedOptions
    },
    {
      title: 'Softwood Lumber Information',
      fields: modalFields.filter((field) => field.section === 'softwood'),
      optionsMap: indexedOptions
    },
    {
      title: 'Steel / Steel Melt Product',
      fields: modalFields.filter((field) => field.section === 'steel'),
      optionsMap: indexedOptions
    },
    {
      title: 'Aluminum Product',
      fields: modalFields.filter((field) => field.section === 'aluminum'),
      optionsMap: indexedOptions
    },
    {
      title: 'Alcohol  under CBMA Product',
      fields: modalFields.filter((field) => field.section === 'alcohol'),
      optionsMap: indexedOptions
    }
  ]

  const formSectionsDuty = [
    {
      title: 'HTS',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedName}.tariffList`,
      tableComponent: DataTableSection,
      tableFields: tariff,
      maxRows: 999,
      disableAdd: true
    },
    {
      title: 'PGA',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedName}.pgaList`,
      tableComponent: DataTableSection,
      tableFields: pga,
      maxRows: 999,
      customColumn: operationColumn,
      data: pgaData
    }
  ]

  const formSectionsTscaOds = [
    {
      title: 'Header',
      fields: pgaField.filter((field) => field.section === 'header'),
      optionsMap: indexedPgaOptionMap
    },
    {
      title: 'Certifying Individual',
      fields: pgaField.filter((field) => field.section === 'individual')
    }
  ]

  const formSectionsLaceyAct = [
    {
      title: 'Header',
      fields: pgaField.filter((field) => field.section === 'laceyHeader'),
      optionsMap: indexedPgaOptionMap
    },
    {
      title: 'Parties & Point of Contact',
      fields: pgaField.filter((field) => field.section === 'laceyParty'),
      optionsMap: indexedPgaOptionMap
    },
    {
      title: 'Contact Info',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedPgaName}.contactInfoList`,
      tableComponent: DataTableSection,
      tableFields: pgaLaceyActPartyContactFields,
      maxRows: 999
    },
    {
      title: 'Constituent & Harvest Country',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedPgaName}.constituentHarvestCountries`,
      tableComponent: DataTableSection,
      tableFields: pgaLaceyActHarvestFields,
      optionsMap: harvestCountryOptionMap,
      maxRows: 999
    },
    {
      title: 'Container Number',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedPgaName}.containerList`,
      tableComponent: DataTableSection,
      tableFields: pgaContainer,
      maxRows: 999
    }
  ]

  const formSectionsFda = [
    {
      title: 'Header',
      fields: pgaField.filter((field) => field.section === 'fdaHeader'),
      optionsMap: indexedPgaOptionMap
    },
    {
      title: 'Country of Origin',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedPgaName}.originCountryList`,
      tableComponent: DataTableSection,
      tableFields: fdaCountryOrigin,
      optionsMap: countryOriginOptionsMap,
      maxRows: 999
    },
    {
      title: 'Packaging',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedPgaName}.packagingList`,
      tableComponent: DataTableSection,
      tableFields: fdaPackaging,
      maxRows: 999
    },
    {
      title: 'Affirmation of Compliance',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedPgaName}.complianceList`,
      tableComponent: DataTableSection,
      tableFields: fdaAffirmationCompliance,
      maxRows: 999
    },
    {
      title: 'Lot Information',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedPgaName}.lotInformationList`,
      tableComponent: DataTableSection,
      tableFields: [...fdaLotInformation],
      optionsMap: lotInfoOptionsMap,
      maxRows: 999,
      condition: (formData, sectionData) => !['DRU'].includes(sectionData?.programCode)
    },
    {
      title: 'Lot Information',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedPgaName}.lotInformationList`,
      tableComponent: DataTableSection,
      tableFields: [...fdaLotInformation, ...fdaLotInformationDRU],
      optionsMap: lotInfoOptionsMap,
      maxRows: 999,
      condition: (formData, sectionData) => ['DRU'].includes(sectionData?.programCode)
    },
    {
      title: 'Parties & Point of Contact',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedPgaName}.partyContactList`,
      tableComponent: DataTableSection,
      tableFields: fdaPartiesPointContact,
      optionsMap: partiesOptionsMap,
      maxRows: 999
    },
    {
      title: 'Private Owned Vehicle Information',
      fields: pgaField.filter((field) => field.section === 'fdaVehicle'),
      optionsMap: indexedPgaOptionMap,
      condition: (formData, sectionData) => ['FOO'].includes(sectionData?.programCode) && !sectionData?.pnConfirmationNumber
    },
    {
      title: 'Acidified and Low Acid Canned Food (LACF)',
      fields: pgaField.filter((field) => field.section === 'fdaLACF'),
      condition: (formData, sectionData) => ['FOO'].includes(sectionData?.programCode)
    },
    {
      title: 'Constituent',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedPgaName}.constituentList`,
      tableComponent: DataTableSection,
      tableFields: fdaConstituent,
      maxRows: 999,
      condition: (formData, sectionData) => ['BIO', 'DRU', 'VNE'].includes(sectionData?.programCode)
    },
    {
      title: 'Containers',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${indexedPgaName}.fdaContainerList`,
      tableComponent: DataTableSection,
      tableFields: fdaContainer,
      maxRows: 999,
      condition: (formData, sectionData) => ['BIO', 'COS', 'FOO', 'DEV', 'RAD'].includes(sectionData?.programCode)
    },
    {
      title: 'Remarks',
      fields: pgaField.filter((field) => field.section === 'fdaRemarks'),
    },
  ]

  return (<>
      {/* Error Snackbar */}
      <SnackBar
        title='Missing Data'
        type='error'
        open={showErrorSnackbar}
        onClose={() => setShowErrorSnackbar(false)}
        duration={10000}
      >
        Nothing to add.
      </SnackBar>

      <SnackBar
        title='Error'
        type='error'
        open={showFormErrorSnackbar}
        onClose={() => setShowFormErrorSnackbar(false)}
        duration={10000}
      >
        Internal error occured, please try again later.
      </SnackBar>

      <SnackBar
        title='Saved successfully'
        type='success'
        open={showSuccessSnackbar}
        onClose={() => setShowSuccessSnackbar(false)}
        duration={10000}
      >
        Line Item saved successfully
      </SnackBar>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='100dvw'
        fullWidth
        ref={dialogRef}
        PaperProps={{
          sx: {
            maxHeight: '90vh'
          }
        }}
        TransitionProps={{
          onEntered: () => {
            // Comprehensive cleanup of any remaining focused elements in main wrapper
            const mainWrapper = document.querySelector('.mainwrapper')
            if (mainWrapper) {
              // Find and blur any focused elements
              const focusedElements = mainWrapper.querySelectorAll(':focus')
              focusedElements.forEach((element) => {
                element.blur()
              })

              // Extra safety: blur any MUI buttons that might be retaining focus
              const muiButtons = mainWrapper.querySelectorAll(
                '.MuiButton-root, .MuiButtonBase-root'
              )
              muiButtons.forEach((button) => {
                if (document.activeElement === button) {
                  button.blur()
                }
              })
            }

            // Ensure focus is properly set in the modal
            setTimeout(() => {
              const dialog = dialogRef.current
              if (dialog) {
                const firstFocusable = dialog.querySelector(
                  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                )
                if (firstFocusable) {
                  firstFocusable.focus()
                }
              }
            }, 10)
          }
        }}
        // Ensure proper focus containment
        disableEnforceFocus={false}
        disableAutoFocus={false}
        disableRestoreFocus={false}
        onKeyDown={handleKeyDown}
        aria-labelledby='modal-title'
        aria-describedby='modal-description'
        role='dialog'
      >
        <DialogTitle
          id='modal-title'
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {`${isEditing ? 'Edit' : 'Add'} ${title}`}
          <IconButton
            aria-label={`Close ${isEditing ? 'edit' : 'add'} ${title} modal`}
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500]
            }}
            title='Close modal'
          >
            <Box
              component={FontAwesomeIcon}
              icon={faClose}
              sx={{ width: '1em' }}
            />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers id='modal-description'>
          <Box sx={{ py: 2 }}>
            <TabContext value={value}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList
                  onChange={handleChange}
                  aria-label='lab API tabs example'
                >
                  <Tab label='Basic' value='basic' />
                  <Tab label='Duty & Additional' value='duty' />
                </TabList>
              </Box>
              <TabPanel value='basic'>
                {formSections.map((section) => (
                  <RenderFormSection
                    section={section}
                    optionsMap={section.optionsMap ? section.optionsMap : {}}
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    arrayKey={indexedName}
                    setErrors={setErrors}
                    setHasUnsaved={setHasUnsaved}
                    globalAccordionAction={null} // Not needed for modal
                  />
                ))}
              </TabPanel>
              <TabPanel value='duty'>
                {loading ? (
                  <div style={{ position: 'relative', height: '200px' }}>
                    {' '}
                    <Spinner />{' '}
                  </div>
                ) : (
                  <div>
                    {formSectionsDuty.map((section) => (
                      <>
                      <RenderFormSection
                        section={section}
                        optionsMap={section.optionsMap ?? {}}
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        arrayKey={indexedName}
                        setErrors={setErrors}
                        setHasUnsaved={setHasUnsaved}
                        globalAccordionAction={null}
                      />
                      {section.title === 'HTS' && (
                        <Button sx={{ minWidth: 150, px: 3, py: 1 }}
                                onClick={handleDutyCalculation}
                                startIcon={dataLoading && <FontAwesomeIcon icon={faSpinnerThird} spin />}
                                disabled={dataLoading} variant='contained'
                        >
                          Calculate Duty
                        </Button>
                      )}

                      {display && section.title === 'HTS' && <Alert severity="info" sx={{ mt:2, mb: 2 }}>
                            <AlertTitle>Note</AlertTitle>
                            {rhf.getValues(`${indexedName}`)?.htsCode
                              ?.countervailingDutyFlag && (
                              <>
                                Countervailing Duty may be required.
                                <br />
                              </>
                            )}

                            {rhf.getValues(`${indexedName}`)?.htsCode
                              ?.antidumpingDutyFlag && (
                              <>
                                Antidumping Duty may be required.
                                <br />
                              </>
                            )}

                            {rhf.getValues(`${indexedName}`)?.htsCode
                              ?.quotaIndicator && (
                              <>
                                Quota may be required.
                                <br />
                              </>
                            )}

                            {rhf.getValues(`${indexedName}`)?.htsCode
                              ?.miscPermitLicenseIndicator && (
                              <>
                                Permit/License{' '}
                                {
                                  rhf.getValues(`${indexedName}`)?.htsCode
                                    ?.miscPermitLicenseIndicator
                                }{' '}
                                may be required.
                              </>
                            )}
                          </Alert>
                        }
                      </>
                    ))}
                    {rhf
                      .getValues(`${indexedName}`)
                      ?.htsCode?.pgaFlags?.trim() && (
                      <Alert severity='info' icon={false} sx={{ mb: 2 }}>
                        PGA Flag:{' '}
                        {rhf.getValues(`${indexedName}`)?.htsCode?.pgaFlags}
                      </Alert>
                    )}
                  </div>
                )}
              </TabPanel>
            </TabContext>
          </Box>
        </DialogContent>

        <DialogActions sx={{ pt: 3 }}>    
        {typeof footerRender === 'function'
          ? footerRender({
              isEditing,
              loading,
              onPrimary: handleSave,
             onCancel: handleClose,
              onDelete
            })
          : (
          <Buttons
            advance={
              <Button
                onClick={handleSave}
                variant='contained'
                title={isEditing ? 'Save changes' : 'Add this record'}
                disabled={loading}
                startIcon={
                  <FontAwesomeIcon
                    icon={
                      loading ? faSpinnerThird : isEditing ? saveIcon : faPlus
                    }
                    width={16}
                    spin={loading}
                  />
                }
              >
                {isEditing ? 'Save' : 'Add'}
              </Button>
            }
            other={
              <>
                <Button
                  variant='outlined'
                  onClick={handleClose}
                  title='Go back'
                >
                  Cancel
                </Button>
                {isEditing && (
                  <Button
                    variant='outlined'
                    onClick={onDelete}
                    color='error'
                    title='Proceed with Caution: Delete record'
                  >
                    Delete
                  </Button>
                )}
              </>
            }
          />
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        maxWidth='90dvw'
        fullWidth
        onClose={handleEditDialogClose}
        closeAfterTransition={false}
      >
        {formSectionPga === 'FDA' && (
          <div>
            <DialogTitle
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}
            >
              FDA
              <IconButton onClick={handleEditDialogClose} sx={{ ml: 'auto' }}>
                <FontAwesomeIcon icon={faClose} width='1em' />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ px: 3 }}>
              {formSectionsFda.map((section) => (
                <RenderFormSection
                  section={section}
                  formData={formData}
                  setFormData={setFormData}
                  optionsMap={section.optionsMap ? section.optionsMap : {}}
                  errors={errors}
                  setErrors={setErrors}
                  setHasUnsaved={setHasUnsaved}
                  globalAccordionAction={null} // Not needed for modal
                  arrayKey={indexedPgaName}
                />
              ))}
            </DialogContent>
          </div>
        )}

        {formSectionPga === 'TSCA ODS' && (
          <div>
            <DialogTitle
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}
            >
              TSCA & ODS
              <IconButton onClick={handleEditDialogClose} sx={{ ml: 'auto' }}>
                <FontAwesomeIcon icon={faClose} width='1em' />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ px: 3 }}>
              {formSectionsTscaOds.map((section) => (
                <RenderFormSection
                  section={section}
                  formData={formData}
                  setFormData={setFormData}
                  optionsMap={section.optionsMap ? section.optionsMap : {}}
                  errors={errors}
                  setErrors={setErrors}
                  setHasUnsaved={setHasUnsaved}
                  globalAccordionAction={null} // Not needed for modal
                  arrayKey={indexedPgaName}
                />
              ))}
            </DialogContent>
          </div>
        )}

        {formSectionPga === 'Lacey Act' && (
          <div>
            <DialogTitle
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}
            >
              Lacey Act
              <IconButton onClick={handleEditDialogClose} sx={{ ml: 'auto' }}>
                <FontAwesomeIcon icon={faClose} width='1em' />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ px: 3 }}>
              {formSectionsLaceyAct.map((section) => (
                <RenderFormSection
                  section={section}
                  formData={formData}
                  setFormData={setFormData}
                  optionsMap={section.optionsMap ? section.optionsMap : {}}
                  errors={errors}
                  setErrors={setErrors}
                  setHasUnsaved={setHasUnsaved}
                  globalAccordionAction={null} // Not needed for modal
                  arrayKey={indexedPgaName}
                />
              ))}
            </DialogContent>
          </div>
        )}
        <DialogActions sx={{ pt: 2.5 }}>
          <Button
            sx={{ minWidth: 150, px: 3, py: 1 }}
            onClick={() => setEditDialogOpen(false)}
            startIcon={false && <FontAwesomeIcon icon={faSpinnerThird} spin />}
            disabled={false}
            variant='contained'
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {isSKUModal && (
                  <SKUMgmtLookupDialog
        open={SKUlookupDialogOpen}
        onClose={() => setSKULookupDialogOpen(false)}
        onSelect={handleSKUSelect}
        title="SKU List"
        initialSearchText={lookupSearchText}
      />
    )}

      <Dialog open={openAddHtsDialog} onClose={() => setOpenAddHtsDialog(false)}>
        <DialogTitle>Add HTS</DialogTitle>
        <DialogContent
          dividers
          id="view-7501-print-options-content"
          sx={{
            px: {
              xs: 2,
              sm: 4,
            },
          }}
        >
          <Box>
            <TextField
              label="HTS"
              id="standard-start-adornment"
              sx={{ m: 1, width: '25ch' }}
              variant="standard"
              value={selectedRow?.tariffNumber}
              disabled
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ pt: 3, justifyContent: 'center' }}>
          <Button onClick={() => setOpenAddHtsDialog(false)}
                  color="error"
                  disabled={loading}>
            Cancel
          </Button>
          <Button onClick={() => handleHtsAdd(selectedRow)}>Add</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EditESLineModal
